import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Addiction v2 API client (api-addiction-v2-service). Mirrors the CC v2 /
 * Diagnosis v2 / Medicine v2 clients.
 *
 * Contract notes:
 *
 * 1. The addiction list is fixed at 4 entries (Smoking / Alcohol / Drugs /
 *    Tobacco). There's no add-new-entry or delete endpoint. `list()` seeds a
 *    `never` starter set on the server if no doc exists for the patient yet.
 *
 * 2. Every mutating call needs `expectedVersion` (the last version the caller
 *    saw). On mismatch the server returns 409 with a fresh doc.
 *
 * 3. Status enum is `"regularly" | "occasionally" | "never" | "quit"`. When
 *    the submitted status is `never` the server strips detail fields
 *    (startedWhen / totalInterval / frequency) even if the client sent them.
 *
 * 4. Trend + mark-history-incorrect endpoints landed Aug 2026 — the
 *    per-addiction StatusHistory that used to be compliance-only is now
 *    exposed via GET /:patientId/:key/trend, and a reviewer can flag one
 *    row via POST /:patientId/:key/history/:index/incorrect. Marking is
 *    audit-only: the live Addiction record is untouched.
 */
class AddictionService {
  constructor() {
    this.apiService = new APIService();
  }

  list(patientId, onResponse, onError) {
    const url = APIConfig.addictionV2.list.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Add-flow save — the full detail write triggered from the "Add addiction"
   * workspace. Body shape matches beans.AddRequest:
   *   { status, startedWhen: {years, months, days},
   *     totalInterval: {years, months, days}, frequency,
   *     description?, expectedVersion }
   */
  add(patientId, key, body, onResponse, onError) {
    const url = APIConfig.addictionV2.add.format(patientId, key);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Update-flow status change — kept for backwards-compat with any
   * caller still hitting /status. The unified Update modal (Aug 2026)
   * uses add() instead since it always sends the full body.
   */
  updateStatus(patientId, key, body, onResponse, onError) {
    const url = APIConfig.addictionV2.updateStatus.format(patientId, key);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Trend — per-addiction audit (StatusHistory with actor names
   * enriched server-side). Powers the View trend modal.
   */
  trend(patientId, key, onResponse, onError) {
    const url = APIConfig.addictionV2.trend.format(patientId, key);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Mark one history row as incorrect. `index` is the row's position in
   * StatusHistory as returned by trend(). Audit-only. Body:
   *   { expectedVersion }
   */
  markHistoryIncorrect(patientId, key, index, expectedVersion, onResponse, onError) {
    const url = APIConfig.addictionV2.markHistoryIncorrect.format(patientId, key, index);
    return this.apiService.post(
      url,
      { expectedVersion: expectedVersion || 0 },
      onResponse,
      onError
    );
  }
}

export default AddictionService;
